import { ReactElement, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Filter from '../controls/Filter';
import Button from '../controls/Button';
import AlertRulesTable from '../components/AlertRules';
import CreateAlertRuleModal from '../modals/CreateAlertRule';
import { useData } from '../context/data.context';
import { ActionData, AlertRule } from '../constants/telosair-interfaces';
import { deleteAlertRule } from '../services/dashboard-service';
import Loader from '../components/loader';
import { TOAST_MESSAGES } from '../constants/global-constants';
import PlanGate from '../components/PlanGate';

/**
 * Alert rules Page Component. The Goal of these page is to show all the alert rules and create a new alert rule.
 * @returns - Alert rules Page.
 */
export default function AlertRulesPage(): ReactElement {
  const { alertRules, fetchAlertRules } = useData();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [actionData, setActionData] = useState<ActionData<AlertRule>>({} as ActionData<AlertRule>);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAlertRules();
  }, []);

  /**
   * Function to handle Modal close state.
   * @param refresh - Boolean value to check if data needs to be refreshed.
   */
  const handleModalClose = (refresh?: boolean): void => {
    if (refresh) {
      fetchAlertRules();
    }
    setIsAddModalOpen(false);
    setActionData({} as ActionData<AlertRule>);
  };

  /**
   * Function to delete a group.
   * @param id - ID of the group.
   */
  const handleDeleteAlertRule = async (id: number): Promise<void> => {
    try {
      setLoading(true);
      await deleteAlertRule(id);
      await fetchAlertRules();
      setLoading(false);
      toast.error(TOAST_MESSAGES.deleted);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast.error(TOAST_MESSAGES.error);
    }
  };

  /**
   * Function to operate delete and edit group.
   * @param action - Action data needs type and group data.
   */
  const handleAction = async (action: ActionData<AlertRule>): Promise<void> => {
    if (action.type === 'delete') {
      handleDeleteAlertRule(action.data.id);
    } else {
      setActionData(action);
      setIsAddModalOpen(true);
    }
  };

  return (
    <PlanGate requiredPlan="Advanced">
      <>
      <div className="flex flex-col sm:flex-row gap-4 justify-between rounded bg-white dark:bg-container-500 p-4 mt-4">
        <div className="flex items-center space-x-4">
          <Filter position="left" />
        </div>
        <div className="flex gap-2">
          <Button theme="primary" size="sm" name="View Analytics" isBlockButton />
          <Button
            onClick={() => setIsAddModalOpen(true)}
            theme="secondary-gray"
            size="sm"
            name="Create Alert Rules"
            isBlockButton
          />
        </div>
      </div>
      {loading && (
        <div>
          <Loader />
        </div>
      )}
      <div className="gap-0 xl:gap-4 my-4 mx-0 lg:mx-0 mb-4">
        <AlertRulesTable
          alertRulesArray={alertRules.sort((a, b) => a.id - b.id)}
          handleAction={handleAction}
        />
      </div>
      {isAddModalOpen && (
        <CreateAlertRuleModal handleClose={handleModalClose} actionData={actionData} />
      )}
      </>
    </PlanGate>
  );
}
